// The map camera: pan and zoom as a transform, never as a re-upload.
//
// The board is 320x320 and lives in one texture. Panning and zooming change two
// uniforms — the board coordinate at the top-left of the viewport, and how many
// device pixels one board pixel occupies — and nothing is uploaded, re-packed or
// re-tiled. That is the whole reason the board is one texture rather than a tile
// grid: at 102,400 pixels the entire map fits in less GPU memory than a single
// 512x512 photograph, so there is nothing to stream.
//
// Everything in this file is pure arithmetic with no GL calls, because the
// screen-to-board mapping is what decides which pixel a click lands on and that
// is worth testing without a canvas.

import { BoardH, BoardW, ChunkSize, ChunksX, ChunksY, type PixelID } from '../wire/core';
import { ChunkSet } from '../wire/chunkset';

export interface Viewport {
  /** Device pixels, i.e. CSS pixels times devicePixelRatio. */
  width: number;
  height: number;
}

export interface CameraOptions {
  /** Device pixels per board pixel at the closest zoom. */
  maxScale?: number;
  /**
   * The zoomed-out limit is derived, not fixed: it is whatever makes the whole
   * board fit, so a viewport of any shape stops exactly when the map is fully
   * visible instead of at some constant that is wrong on half of them.
   */
  minScaleSlack?: number;
}

export class Camera {
  /** Board coordinate at the top-left corner of the viewport. */
  originX = 0;
  originY = 0;
  /** Device pixels per board pixel. */
  scale = 2;

  viewport: Viewport = { width: 1, height: 1 };

  private readonly maxScale: number;
  private readonly minScaleSlack: number;

  constructor(opts: CameraOptions = {}) {
    this.maxScale = opts.maxScale ?? 48;
    this.minScaleSlack = opts.minScaleSlack ?? 1;
  }

  resize(width: number, height: number): void {
    this.viewport = { width: Math.max(1, width), height: Math.max(1, height) };
    this.clamp();
  }

  /** The zoomed-out limit: whatever makes the whole board fit. */
  minScale(): number {
    const { width, height } = this.viewport;
    return Math.min(width / BoardW, height / BoardH) / this.minScaleSlack;
  }

  /** How much board is visible, in board pixels. */
  visibleSize(): { w: number; h: number } {
    return { w: this.viewport.width / this.scale, h: this.viewport.height / this.scale };
  }

  /** Board coordinate under a device-pixel position, measured from the top-left of the canvas. */
  screenToBoard(sx: number, sy: number): { x: number; y: number } {
    return { x: this.originX + sx / this.scale, y: this.originY + sy / this.scale };
  }

  /** Device-pixel position of a board coordinate. */
  boardToScreen(bx: number, by: number): { x: number; y: number } {
    return { x: (bx - this.originX) * this.scale, y: (by - this.originY) * this.scale };
  }

  /** The pixel under a device-pixel position, or -1 off the board. */
  pixelAt(sx: number, sy: number): PixelID {
    const b = this.screenToBoard(sx, sy);
    const x = Math.floor(b.x);
    const y = Math.floor(b.y);
    if (x < 0 || y < 0 || x >= BoardW || y >= BoardH) return -1;
    return y * BoardW + x;
  }

  /** Drags the map by a device-pixel offset. */
  panBy(dx: number, dy: number): void {
    this.originX -= dx / this.scale;
    this.originY -= dy / this.scale;
    this.clamp();
  }

  centreOn(bx: number, by: number): void {
    const v = this.visibleSize();
    this.originX = bx - v.w / 2;
    this.originY = by - v.h / 2;
    this.clamp();
  }

  /**
   * Zooms about a device-pixel anchor, keeping the board point under it fixed.
   * That is what makes wheel-zoom feel like the map is under the cursor rather
   * than sliding out from under it.
   */
  zoomAt(sx: number, sy: number, factor: number): void {
    const before = this.screenToBoard(sx, sy);
    const min = this.minScale();
    this.scale = Math.min(this.maxScale, Math.max(min, this.scale * factor));
    const after = this.screenToBoard(sx, sy);
    this.originX += before.x - after.x;
    this.originY += before.y - after.y;
    this.clamp();
  }

  /** Fits the whole board in the viewport. */
  fit(): void {
    this.scale = this.minScale();
    this.centreOn(BoardW / 2, BoardH / 2);
  }

  /**
   * Keeps the board in view.
   *
   * When the board is smaller than the viewport on an axis it is CENTRED on that
   * axis rather than pinned to an edge — a map that can be dragged off into empty
   * space reads as broken, and pinning it to the left looks like a layout bug.
   */
  clamp(): void {
    const v = this.visibleSize();
    if (v.w >= BoardW) this.originX = (BoardW - v.w) / 2;
    else this.originX = Math.min(Math.max(this.originX, 0), BoardW - v.w);
    if (v.h >= BoardH) this.originY = (BoardH - v.h) / 2;
    else this.originY = Math.min(Math.max(this.originY, 0), BoardH - v.h);
  }

  /**
   * The chunks the viewport covers plus a margin, which is what SPEC §3 has a
   * client subscribe to: "visible chunks plus a one-chunk margin, and a
   * subscribe/unsubscribe diff on pan".
   *
   * The margin is why a drag does not show a frame of stale territory at the
   * leading edge: the chunk about to scroll in is already subscribed.
   */
  visibleChunks(margin = 1): ChunkSet {
    const v = this.visibleSize();
    const cx0 = Math.max(0, Math.floor(this.originX / ChunkSize) - margin);
    const cy0 = Math.max(0, Math.floor(this.originY / ChunkSize) - margin);
    const cx1 = Math.min(ChunksX - 1, Math.floor((this.originX + v.w - 1e-6) / ChunkSize) + margin);
    const cy1 = Math.min(ChunksY - 1, Math.floor((this.originY + v.h - 1e-6) / ChunkSize) + margin);
    const s = new ChunkSet();
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) s.set(cy * ChunksX + cx);
    }
    return s;
  }

  /**
   * Whether the view is far enough out that the per-pixel tier is not worth
   * carrying. §3: a zoomed-out client takes per-chunk aggregates instead of
   * per-pixel deltas, and the honest threshold is "one board pixel is smaller
   * than one device pixel", because below that the deltas are drawing detail the
   * screen cannot show.
   */
  isWorldZoom(): boolean {
    return this.scale < 1;
  }
}
